import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { connectNotificacoesStream } from "@spark/services/notificacoes/stream";
import { getNotificacoes } from "@spark/services/notificacoes";
import {
  alertarAtrasoDescarga,
  alertasAtivosNoStorage,
  deveAlertar,
  ehNotificacaoAlertaDescarga,
  exibirNotificacaoSO,
  filtrarAtrasosNaoLidos,
  persistirAlertasAtivos,
  prepararSinteseVoz,
  solicitarPermissaoNotificacaoSO,
} from "@spark/services/notificacoes/alerta";
import { inscreverPush } from "@spark/services/notificacoes/push";
import { useAuth } from "./AuthContext";
import { COGNITO_GROUP_NOTIFICACOES_DESCARGA } from "@/utils/cognitoGroups";
import DescargaAtrasoAlertModal from "@/containers/Notificacoes/DescargaAtrasoAlertModal";

const NotificacoesAlertContext = createContext(null);

const ENABLE_NOTIFICACOES = process.env.REACT_APP_NOTIFICACOES_ENABLED === "true";
const FILTRO_NAO_LIDAS = { Lida: false };

export const NotificacoesAlertProvider = ({ children }) => {
  const { isAuthenticated, hasCognitoGroup } = useAuth();
  const podeAlertarDescarga = hasCognitoGroup(COGNITO_GROUP_NOTIFICACOES_DESCARGA);
  const [modalAberto, setModalAberto] = useState(false);
  const [atrasosNaoLidos, setAtrasosNaoLidos] = useState([]);
  const [alertasAtivos, setAlertasAtivos] = useState(alertasAtivosNoStorage);
  const [notificacaoNovaVersao, setNotificacaoNovaVersao] = useState(0);
  const idsAlertadosSessaoRef = useRef(new Set());

  const recarregarAtrasosNaoLidos = useCallback(async () => {
    if (!isAuthenticated || !ENABLE_NOTIFICACOES || !podeAlertarDescarga) {
      setAtrasosNaoLidos([]);
      return [];
    }

    try {
      const response = await getNotificacoes({
        Limit: 50,
        Offset: 0,
        ...FILTRO_NAO_LIDAS,
      });
      const lista = filtrarAtrasosNaoLidos(response?.registros);
      setAtrasosNaoLidos(lista);
      return lista;
    } catch {
      setAtrasosNaoLidos([]);
      return [];
    }
  }, [isAuthenticated, podeAlertarDescarga]);

  const abrirModalAtrasos = useCallback(() => {
    if (!podeAlertarDescarga) {
      return;
    }
    setModalAberto(true);
  }, [podeAlertarDescarga]);

  const fecharModalAtrasos = useCallback(() => {
    setModalAberto(false);
  }, []);

  const removerAtrasoDaLista = useCallback((notificacaoId) => {
    setAtrasosNaoLidos((prev) => prev.filter((item) => item.Id !== notificacaoId));
    setNotificacaoNovaVersao((prev) => prev + 1);
  }, []);

  const ativarAlertas = useCallback(async () => {
    if (!podeAlertarDescarga) {
      return;
    }
    persistirAlertasAtivos();
    setAlertasAtivos(true);
    await prepararSinteseVoz();
    await solicitarPermissaoNotificacaoSO();
    // Push depende da permissão concedida acima; nunca lança (degrada para SSE).
    await inscreverPush();
  }, [podeAlertarDescarga]);

  const processarAlertaDescarga = useCallback(
    async (notificacao) => {
      if (!podeAlertarDescarga || !notificacao?.Id) {
        return;
      }

      if (idsAlertadosSessaoRef.current.has(notificacao.Id)) {
        await recarregarAtrasosNaoLidos();
        setModalAberto(true);
        return;
      }

      idsAlertadosSessaoRef.current.add(notificacao.Id);

      await recarregarAtrasosNaoLidos();
      setModalAberto(true);

      if (deveAlertar(notificacao)) {
        await alertarAtrasoDescarga(notificacao);
        exibirNotificacaoSO(notificacao, abrirModalAtrasos);
      }
    },
    [abrirModalAtrasos, podeAlertarDescarga, recarregarAtrasosNaoLidos]
  );

  useEffect(() => {
    if (!isAuthenticated || !ENABLE_NOTIFICACOES) {
      return undefined;
    }

    const disconnect = connectNotificacoesStream((evt) => {
      if (evt?.type !== "NOTIFICACAO_NOVA") {
        return;
      }

      setNotificacaoNovaVersao((prev) => prev + 1);

      const data = evt?.data;
      if (podeAlertarDescarga && ehNotificacaoAlertaDescarga(data)) {
        processarAlertaDescarga(data);
      }
    });

    return disconnect;
  }, [isAuthenticated, podeAlertarDescarga, processarAlertaDescarga]);

  useEffect(() => {
    if (!isAuthenticated) {
      setAtrasosNaoLidos([]);
      setModalAberto(false);
      idsAlertadosSessaoRef.current = new Set();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!podeAlertarDescarga) {
      setAtrasosNaoLidos([]);
      setModalAberto(false);
      idsAlertadosSessaoRef.current = new Set();
    }
  }, [podeAlertarDescarga]);

  // Reinscrição silenciosa a cada sessão: renova a subscription no backend
  // (grupos/token podem mudar) sem exigir novo gesto do usuário.
  useEffect(() => {
    if (!isAuthenticated || !ENABLE_NOTIFICACOES || !alertasAtivos || !podeAlertarDescarga) {
      return;
    }
    inscreverPush();
  }, [isAuthenticated, alertasAtivos, podeAlertarDescarga]);

  const sinalizarAtualizacaoNotificacoes = useCallback(() => {
    setNotificacaoNovaVersao((prev) => prev + 1);
  }, []);

  const bannerVisivel = Boolean(
    ENABLE_NOTIFICACOES && isAuthenticated && podeAlertarDescarga && !alertasAtivos
  );

  const valor = useMemo(
    () => ({
      bannerVisivel,
      modalAberto,
      atrasosNaoLidos,
      totalAtrasosNaoLidos: atrasosNaoLidos.length,
      alertasAtivos,
      notificacaoNovaVersao,
      abrirModalAtrasos,
      fecharModalAtrasos,
      recarregarAtrasosNaoLidos,
      removerAtrasoDaLista,
      ativarAlertas,
      sinalizarAtualizacaoNotificacoes,
      setModalAberto,
    }),
    [
      bannerVisivel,
      modalAberto,
      atrasosNaoLidos,
      alertasAtivos,
      notificacaoNovaVersao,
      abrirModalAtrasos,
      fecharModalAtrasos,
      recarregarAtrasosNaoLidos,
      removerAtrasoDaLista,
      ativarAlertas,
      sinalizarAtualizacaoNotificacoes,
    ]
  );

  return (
    <NotificacoesAlertContext.Provider value={valor}>
      {children}
      {bannerVisivel && <AtivarAlertasBanner onAtivar={ativarAlertas} />}
      <DescargaAtrasoAlertModal />
    </NotificacoesAlertContext.Provider>
  );
};

export const useNotificacoesAlert = () => {
  const contexto = useContext(NotificacoesAlertContext);
  if (!contexto) {
    throw new Error("useNotificacoesAlert deve ser usado dentro de NotificacoesAlertProvider");
  }
  return contexto;
};

const AtivarAlertasBanner = ({ onAtivar }) => (
  <BannerContainer>
    <BannerText>Ative alertas sonoros de espera e atraso na descarga.</BannerText>
    <BannerButton type="button" onClick={onAtivar}>
      Ativar alertas
    </BannerButton>
  </BannerContainer>
);

const BannerContainer = styled.div`
  position: fixed;
  bottom: 16px;
  right: 16px;
  z-index: 1050;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: #fff3f3;
  border: 1px solid #e0536f;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
`;

const BannerText = styled.span`
  font-size: 12px;
  color: #333;
`;

const BannerButton = styled.button`
  border: none;
  background: #e0536f;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: 4px;
  cursor: pointer;
`;
